import React, { useMemo, Component } from 'react';
import { Activity, Database, TrendingUp, Zap, RefreshCw, Trash2, AlertTriangle, ArrowRight, Layers, CheckCircle2 } from 'lucide-react';
import { AppState, GroupId, Question } from '../../../types';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { MODULES } from '../../../data/modules';
import { getAllSeedQuestions } from '../../../data/seedConsolidator';
interface DevSystemPanelProps {
  state: AppState;
  questions: Question[];
  selectedGroup: GroupId;
  onJumpToLevel: (level: number) => void;
  onResetProgress: () => void;
  refreshKey?: number;
  onRefresh?: () => void;
  onOpenGovernance?: () => void;
}
export function DevSystemPanel({
  state,
  questions,
  onJumpToLevel,
  onResetProgress,
  refreshKey = 0,
  onRefresh,
  onOpenGovernance
}: DevSystemPanelProps) {
  // Calculate system health
  const systemHealth = useMemo(() => {
    const totalModules = MODULES.length;
    const activeModules = Object.keys(state.progress).length;
    const totalXP = state.stats.xp;
    const currentLevel = state.stats.level;
    return {
      modulesActive: activeModules,
      totalModules,
      xp: totalXP,
      level: currentLevel,
      questionsTotal: questions.length
    };
  }, [state, questions]);
  // Seed integrity check (lightweight)
  const seedIntegrityStats = useMemo(() => {
    const allSeeds = getAllSeedQuestions();
    const duplicateIds = allSeeds.length - new Set(allSeeds.map(seed => seed.id)).size;
    const missingFamilyId = allSeeds.filter(seed => !seed.familyId).length;
    const missingTier = allSeeds.filter(seed => typeof seed.tier !== 'number').length;
    const totalErrors = duplicateIds + missingFamilyId + missingTier;
    return {
      total: allSeeds.length,
      totalErrors,
      status: totalErrors === 0 ? 'operational' : 'error'
    };
  }, [refreshKey]);
  return <div className="space-y-6" data-id="element-1923">
      {/* System Stats Grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1924">
        <StatCard label="Questions" value={systemHealth.questionsTotal} icon={Database} color="indigo" data-id="element-1925" />
        <StatCard label="Modules" value={`${systemHealth.modulesActive}/${systemHealth.totalModules}`} icon={Layers} color="blue" data-id="element-1926" />
        <StatCard label="Level" value={systemHealth.level} icon={TrendingUp} color="emerald" data-id="element-1927" />
        <StatCard label="Total XP" value={systemHealth.xp.toLocaleString()} icon={Zap} color="amber" data-id="element-1928" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1929">
        {/* Quick Actions */}
        <div className="lg:col-span-2" data-id="element-1930">
          <Card className="p-5 h-full" data-id="element-1931">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1932">
              <Activity className="w-5 h-5 text-slate-500" data-id="element-1933" />
              Developer Actions
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1934">
              <Button onClick={() => onJumpToLevel(1)} variant="secondary" className="justify-start text-sm" data-id="element-1935">
                <RefreshCw className="w-4 h-4 mr-2 text-slate-500" data-id="element-1936" />
                Reset to Level 1
              </Button>
              <Button onClick={() => onJumpToLevel(10)} variant="secondary" className="justify-start text-sm" data-id="element-1937">
                <TrendingUp className="w-4 h-4 mr-2 text-slate-500" data-id="element-1938" />
                Jump to Level 10
              </Button>
              <Button onClick={onResetProgress} variant="secondary" className="justify-start text-sm text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300" data-id="element-1939">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-1940" />
                Reset All Progress
              </Button>
              <Button onClick={onRefresh} variant="ghost" className="justify-start text-sm text-slate-500" data-id="element-1941">
                <RefreshCw className="w-4 h-4 mr-2" data-id="element-1942" />
                Recompute Stats
              </Button>
            </div>
          </Card>
        </div>

        {/* Seed Integrity Summary */}
        <div data-id="element-1943">
          <Card className="p-5 h-full flex flex-col" data-id="element-1944">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1945">
              <Database className="w-5 h-5 text-slate-500" data-id="element-1946" />
              Seed Integrity
            </h3>

            <div className="flex-1 flex flex-col justify-center items-center text-center py-4" data-id="element-1947">
              {seedIntegrityStats.totalErrors === 0 ? <>
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3" data-id="element-1948">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" data-id="element-1949" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-1950">
                    {seedIntegrityStats.total}
                  </div>
                  <p className="text-sm text-slate-500" data-id="element-1951">Valid Seeds</p>
                </> : <>
                  <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mb-3" data-id="element-1952">
                    <AlertTriangle className="w-6 h-6 text-rose-600" data-id="element-1953" />
                  </div>
                  <div className="text-2xl font-bold text-rose-600" data-id="element-1954">
                    {seedIntegrityStats.totalErrors}
                  </div>
                  <p className="text-sm text-slate-500" data-id="element-1955">Structural Errors</p>
                </>}
            </div>

            <div className="mt-auto pt-4 border-t border-slate-100" data-id="element-1956">
              <Button size="sm" variant="secondary" fullWidth onClick={onOpenGovernance} data-id="element-1957">
                View Governance Console
                <ArrowRight className="w-3.5 h-3.5 ml-2" data-id="element-1958" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon: Icon,
  color
}: {
  label: string;
  value: string | number;
  icon: ComponentType<{
    className?: string;
  }>;
  color: 'indigo' | 'blue' | 'emerald' | 'amber';
}) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600'
  };
  return <Card className="p-5 flex items-start justify-between" data-id="element-1959">
      <div data-id="element-1960">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1961">
          {label}
        </p>
        <p className="text-2xl font-black text-slate-900" data-id="element-1962">{value}</p>
      </div>
      <div className={`p-3 rounded-xl ${colors[color]}`} data-id="element-1963">
        <Icon className="w-5 h-5" data-id="element-1964" />
      </div>
    </Card>;
}